import { useMemo } from 'react';

import { useConfiguration } from '@ocx/data-configuration';
import {
  PaymentAccountType,
  PaymentInstrumentFragment,
  PaymentInstrumentStatus,
  useGetPaymentInstrumentsQuery,
} from '@ocx/graphql';

import {
  isApplePayPaymentInstrumentFragment,
  isDefaultPaymentInstrument,
  isGooglePayPaymentInstrumentFragment,
  sanitizePaymentInstrumentFragment,
} from './payment-instruments.utils';
import { ON_DEVICE_ACCOUNT_TYPES, TOKENIZABLE_ACCOUNT_TYPES } from './payment-instruments.constants';
import { PaymentInstrument } from './payment-instruments.types';
import { useOnDevicePaymentInstruments } from './use-on-device-payment-instruments.hook';

export type UsePaymentInstrumentsReturns = {
  loading: boolean;
  refetch(): Promise<unknown>;
  hasPasscode: boolean;
  paymentInstruments: PaymentInstrument[];
  defaultPaymentInstrumentId: string | null;
  isWalletSizeLimitReached: boolean;
  walletSizeLimit: number;
  isWalletEnabled: boolean;
};

type UsePaymentInstrumentsParams = {
  visibleAccountTypes?: Readonly<PaymentAccountType[]>;
  canBeUsedToPayEquals?: boolean;
};

export const usePaymentInstruments = (params: UsePaymentInstrumentsParams = {}): UsePaymentInstrumentsReturns => {
  const {
    sizeLimit: walletSizeLimit,
    // isWalletEnabled === showSavedPaymentInstruments
    enabled: isWalletEnabled,
    prepaidAccountTypeEnabled,
  } = useConfiguration('wallet');

  const { visibleAccountTypes = [...ON_DEVICE_ACCOUNT_TYPES, ...TOKENIZABLE_ACCOUNT_TYPES] } = params;
  const availableAccountTypes = visibleAccountTypes.filter((type) => {
    if (type === 'PREPAID' && !prepaidAccountTypeEnabled) {
      return false;
    }
    return true;
  });
  const showOnDevicePaymentInstruments =
    visibleAccountTypes.includes(PaymentAccountType.ApplePay) ||
    visibleAccountTypes.includes(PaymentAccountType.GooglePay);

  const { data, loading, refetch } = useGetPaymentInstrumentsQuery({
    skip: !isWalletEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const isApplePayDefault = !!data?.customer?.paymentWallet?.find((value) => isApplePayPaymentInstrumentFragment(value))
    ?.isDefault;
  const isGooglePayDefault = !!data?.customer?.paymentWallet?.find((value) =>
    isGooglePayPaymentInstrumentFragment(value),
  )?.isDefault;
  const { loading: onDevicePaymentInstrumentsLoading, paymentInstruments: onDeviceInstruments } =
    useOnDevicePaymentInstruments({ skip: !showOnDevicePaymentInstruments, isApplePayDefault, isGooglePayDefault });

  const paymentInstruments = useMemo(() => {
    // Apple/Google Pay and wallet are disabled
    if (!showOnDevicePaymentInstruments && !isWalletEnabled) {
      return [];
    }

    if (!isWalletEnabled) {
      // Return on device
      return onDeviceInstruments;
    }

    const activeWalletInstruments =
      data?.customer?.paymentWallet?.filter(
        // TODO: graphql schema needs to be fixed for not having that many nullables
        // TODO: active should be an API side filter?
        (instrument): instrument is PaymentInstrumentFragment => {
          if (!instrument) {
            return false;
          }
          if (instrument.status !== PaymentInstrumentStatus.Active) {
            return false;
          }
          return !(isApplePayPaymentInstrumentFragment(instrument) || isGooglePayPaymentInstrumentFragment(instrument));
        },
      ) || [];

    const notOnDeviceInstruments = activeWalletInstruments
      .map((value) => {
        return sanitizePaymentInstrumentFragment(value, {
          availableAccountTypes,
        });
      })
      .sort((a, b) => a.order - b.order)
      // TODO: this should be handled as a disabled UI in the future
      .filter((instrument) => {
        if (typeof params.canBeUsedToPayEquals === 'boolean') {
          return params.canBeUsedToPayEquals === instrument.canBeUsedToPay;
        }
        return true;
      });
    if (!showOnDevicePaymentInstruments) {
      return notOnDeviceInstruments;
    }

    return [...onDeviceInstruments, ...notOnDeviceInstruments];
  }, [
    availableAccountTypes,
    data?.customer?.paymentWallet,
    isWalletEnabled,
    onDeviceInstruments,
    params.canBeUsedToPayEquals,
    showOnDevicePaymentInstruments,
  ]);

  const defaultPaymentInstrumentId: string | null = useMemo(() => {
    const defaultInstrument = paymentInstruments.find((instrument) => {
      return isDefaultPaymentInstrument(instrument) && instrument.canBeUsedToPay;
    });
    // Return Apple Pay for iOS (or Google Pay for Android) as default,
    // This is achieved by pushing ApplePay as a first item in useGetOnDevicePaymentInstruments
    return defaultInstrument?.uuid || onDeviceInstruments[0]?.uuid || null;
  }, [onDeviceInstruments, paymentInstruments]);

  return useMemo(() => {
    return {
      paymentInstruments,
      refetch,
      defaultPaymentInstrumentId,
      loading: loading || onDevicePaymentInstrumentsLoading,
      hasPasscode: data?.customer?.hasPasscode ?? false,
      isWalletSizeLimitReached: paymentInstruments.length - onDeviceInstruments.length >= walletSizeLimit,
      walletSizeLimit,
      isWalletEnabled,
    };
  }, [
    paymentInstruments,
    refetch,
    defaultPaymentInstrumentId,
    loading,
    onDevicePaymentInstrumentsLoading,
    data?.customer?.hasPasscode,
    onDeviceInstruments.length,
    walletSizeLimit,
    isWalletEnabled,
  ]);
};
